/**
 * PlatformIdentityProvider — authoritative source of truth for
 * organizational identity across every COS surface.
 *
 * Wraps the app once. Every page, footer, email preview, schema.org
 * block, and static-copy component reads from this hook instead of
 * hardcoding text. When a new organization deploys COS, editing the
 * identity via the App Center (or `PATCH /api/cos/platform/identity/{id}`)
 * updates every surface simultaneously.
 *
 * Fetches `GET /api/cos/platform/identity/public` — the unauthenticated
 * endpoint that returns `{ primary, commercial_entity }`.
 */
import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { platformIdentityApi } from "../lib/api";

const Ctx = createContext(null);

export function PlatformIdentityProvider({ children }) {
  const [primary, setPrimary] = useState(null);
  const [commercial, setCommercial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const d = await platformIdentityApi.public();
      setPrimary(d.primary || null);
      setCommercial(d.commercial_entity || null);
      setError(null);
    } catch (e) {
      setError(e);
      // Do NOT crash the app — leave `primary` null so components can render
      // fallback copy. In practice this only happens if the backend is
      // completely offline.
      // eslint-disable-next-line no-console
      console.warn("PlatformIdentity: failed to load", e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const value = useMemo(() => ({
    primary, commercial, loading, error, reload: load,
  }), [primary, commercial, loading, error]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Access the platform identity anywhere below the provider. */
export function usePlatformIdentity() {
  const v = useContext(Ctx);
  if (v === null) {
    throw new Error("usePlatformIdentity must be used inside <PlatformIdentityProvider>");
  }
  return v;
}

/** Convenience selector for the most common lookup: display name. */
export function useOrgName() {
  const { primary } = usePlatformIdentity();
  return primary?.organization_name || "Chicago Opportunity Desk";
}
